import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { Check, ClipboardCheck, MessageSquare, X } from 'lucide-react';
import { useState } from 'react';
import { projectDataClient } from '../../../lib/connect/clients';
import { useChatFeedback } from '../ChatFeedbackContext';
function str(params, key) {
    const v = params[key];
    return typeof v === 'string' ? v : '';
}
/**
 * Apply an AI-proposed record change via the projectdata RPC. The call goes
 * through sabaody under the *human's* JWT principal — so the AI never writes
 * directly, and the resulting change is attributed to the approving user (R03).
 */ async function applyRecordWrite(action, params) {
    switch(action){
        case 'update-deliverable-status':
            await projectDataClient.updateDeliverableStatus({
                projectId: str(params, 'projectId'),
                deliverableId: str(params, 'deliverableId'),
                status: str(params, 'status'),
                notes: str(params, 'notes')
            });
            return;
        case 'update-clin-status':
            await projectDataClient.updateCLINStatus({
                projectId: str(params, 'projectId'),
                clinId: str(params, 'clinId'),
                fundingStatus: str(params, 'fundingStatus'),
                notes: str(params, 'notes')
            });
            return;
        case 'update-requirement-status':
            {
                const mapping = params.mapping;
                await projectDataClient.updateRequirementStatus({
                    projectId: str(params, 'projectId'),
                    requirementId: str(params, 'requirementId'),
                    status: str(params, 'status'),
                    notes: str(params, 'notes'),
                    ...mapping && typeof mapping === 'object' ? {
                        mapping: {
                            proposalSection: str(mapping, 'proposalSection'),
                            responseText: str(mapping, 'responseText'),
                            pageReference: str(mapping, 'pageReference')
                        }
                    } : {}
                });
                return;
            }
        case 'create-contract-from-proposal':
            {
                const contractNumber = str(params, 'contractNumber');
                const contractType = str(params, 'contractType');
                await projectDataClient.createContractProjectFromProposal({
                    proposalProjectId: str(params, 'proposalProjectId'),
                    name: str(params, 'name'),
                    description: str(params, 'description'),
                    archiveSourceProposal: params.archiveSourceProposal === true,
                    ...contractNumber || contractType ? {
                        contractData: {
                            contractNumber,
                            contractType
                        }
                    } : {}
                });
                return;
            }
        default:
            throw new Error(`Unknown record-write action: ${action}`);
    }
}
const ACTION_LABELS = {
    'update-deliverable-status': 'Update deliverable status',
    'update-clin-status': 'Update CLIN funding status',
    'update-requirement-status': 'Update requirement status',
    'create-contract-from-proposal': 'Convert proposal → contract'
};
/**
 * Renders an AI-proposed record/status change with Accept/Reject/Comment.
 * Mirrors DocumentWriteCard, but the proposed change is applied via a backend
 * projectdata RPC on accept rather than client-side document operations.
 */ export function RecordWriteCard({ output }) {
    const [status, setStatus] = useState('pending');
    const [execError, setExecError] = useState(null);
    const [commentText, setCommentText] = useState('');
    const [showComment, setShowComment] = useState(false);
    const sendFeedback = useChatFeedback();
    if (!output.success || !output.action || !output.params) {
        return /*#__PURE__*/ _jsx("div", {
            className: "border border-red-200 rounded-lg p-3 bg-red-50",
            children: /*#__PURE__*/ _jsx("div", {
                className: "text-sm text-red-700",
                children: output.error || 'Failed to generate the proposed change.'
            })
        });
    }
    const action = output.action;
    const params = output.params;
    const label = ACTION_LABELS[action] || action;
    const handleAccept = async ()=>{
        setStatus('accepted');
        setExecError(null);
        try {
            await applyRecordWrite(action, params);
        } catch (err) {
            setExecError(err instanceof Error ? err.message : 'Failed to apply the change');
            setStatus('pending');
            return;
        }
        sendFeedback(`User approved and applied the proposed change: ${output.summary || label}. The change was written under the user's account.`);
    };
    const handleReject = ()=>{
        setStatus('rejected');
        sendFeedback(`User rejected the proposed change: ${output.summary || label}. Ask what they would like to do instead.`);
    };
    const handleComment = ()=>{
        if (!commentText.trim()) return;
        setStatus('commented');
        sendFeedback(`User feedback on the proposed change: ${commentText.trim()}`);
        setShowComment(false);
    };
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.assistant.recordWriteCard,
        className: "border border-amber-200 rounded-lg overflow-hidden bg-white",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-100",
                children: [
                    /*#__PURE__*/ _jsx(ClipboardCheck, {
                        className: "w-4 h-4 text-amber-600"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-sm font-medium text-amber-900",
                        children: "Proposed change — approval required"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-xs text-amber-500",
                        children: label
                    })
                ]
            }),
            output.summary && /*#__PURE__*/ _jsx("div", {
                className: "px-3 py-2 text-sm text-gray-700 border-b border-gray-100",
                children: output.summary
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "px-3 py-2 space-y-1 max-h-48 overflow-auto",
                children: Object.entries(params).filter(([, v])=>v !== '' && v !== undefined && v !== null && typeof v !== 'object').map(([k, v])=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex items-start gap-2 text-xs",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "shrink-0 text-gray-500",
                                children: k
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "min-w-0 flex-1 font-mono text-gray-700 truncate",
                                children: String(v)
                            })
                        ]
                    }, k))
            }),
            status === 'pending' && /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 px-3 py-2 border-t border-gray-100 bg-gray-50",
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        "data-testid": Testids.assistant.recordWriteAccept,
                        onClick: handleAccept,
                        className: "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors",
                        children: [
                            /*#__PURE__*/ _jsx(Check, {
                                className: "w-3.5 h-3.5"
                            }),
                            "Accept & apply"
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        "data-testid": Testids.assistant.recordWriteReject,
                        onClick: handleReject,
                        className: "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors",
                        children: [
                            /*#__PURE__*/ _jsx(X, {
                                className: "w-3.5 h-3.5"
                            }),
                            "Reject"
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        onClick: ()=>setShowComment(true),
                        className: "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors",
                        children: [
                            /*#__PURE__*/ _jsx(MessageSquare, {
                                className: "w-3.5 h-3.5"
                            }),
                            "Comment"
                        ]
                    })
                ]
            }),
            showComment && status === 'pending' && /*#__PURE__*/ _jsxs("div", {
                className: "px-3 py-2 border-t border-gray-100 bg-gray-50",
                children: [
                    /*#__PURE__*/ _jsx("textarea", {
                        value: commentText,
                        onChange: (e)=>setCommentText(e.target.value),
                        placeholder: "Tell the AI what to change...",
                        className: "w-full text-xs border border-gray-300 rounded-md p-2 resize-none focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500",
                        rows: 2
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-end gap-2 mt-1.5",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: ()=>setShowComment(false),
                                className: "text-xs text-gray-500 hover:text-gray-700",
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: handleComment,
                                disabled: !commentText.trim(),
                                className: "text-xs font-medium text-amber-600 hover:text-amber-700 disabled:text-gray-400",
                                children: "Send Feedback"
                            })
                        ]
                    })
                ]
            }),
            execError && /*#__PURE__*/ _jsx("div", {
                className: "px-3 py-2 border-t border-red-100 bg-red-50",
                children: /*#__PURE__*/ _jsx("span", {
                    className: "text-xs text-red-700",
                    children: execError
                })
            }),
            status === 'accepted' && !execError && /*#__PURE__*/ _jsx("div", {
                className: "px-3 py-2 border-t border-green-100 bg-green-50",
                children: /*#__PURE__*/ _jsx("span", {
                    className: "text-xs font-medium text-green-700",
                    children: "✓ Change applied"
                })
            }),
            status === 'rejected' && /*#__PURE__*/ _jsx("div", {
                className: "px-3 py-2 border-t border-red-100 bg-red-50",
                children: /*#__PURE__*/ _jsx("span", {
                    className: "text-xs font-medium text-red-700",
                    children: "✗ Change rejected"
                })
            }),
            status === 'commented' && /*#__PURE__*/ _jsx("div", {
                className: "px-3 py-2 border-t border-amber-100 bg-amber-50",
                children: /*#__PURE__*/ _jsx("span", {
                    className: "text-xs font-medium text-amber-700",
                    children: "💬 Feedback sent — waiting for AI"
                })
            })
        ]
    });
}
